import type { ReactNode } from 'react';

import { cn } from '@/lib/utils';
import { Skeleton } from './Skeleton';

/**
 * The tints a tile may wear.
 *
 * Spelled out in full rather than composed from the tone's name — Tailwind
 * scans the source for literal class names, so `bg-${tone}-50` would compile to
 * nothing and the tile would render untinted.
 *
 * These are Tailwind palette shades rather than this project's `var()` tokens
 * on purpose: an opacity modifier works on the former and is silently dropped
 * on the latter, and the whole tile is built out of soft tints.
 */
const TONE = {
  blue: {
    card: 'bg-blue-50/70 dark:bg-blue-950/20 border-blue-200/80 dark:border-blue-900/40',
    icon: 'bg-blue-500/15 text-blue-600 dark:text-blue-400 border-blue-500/25',
    label: 'text-blue-800 dark:text-blue-300',
    value: 'text-blue-600 dark:text-blue-400',
  },
  emerald: {
    card: 'bg-emerald-50/70 dark:bg-emerald-950/20 border-emerald-200/80 dark:border-emerald-900/40',
    icon: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/25',
    label: 'text-emerald-800 dark:text-emerald-300',
    value: 'text-emerald-600 dark:text-emerald-400',
  },
  teal: {
    card: 'bg-teal-50/70 dark:bg-teal-950/20 border-teal-200/80 dark:border-teal-900/40',
    icon: 'bg-teal-500/15 text-teal-600 dark:text-teal-400 border-teal-500/25',
    label: 'text-teal-800 dark:text-teal-300',
    value: 'text-teal-600 dark:text-teal-400',
  },
  sky: {
    card: 'bg-sky-50/70 dark:bg-sky-950/20 border-sky-200/80 dark:border-sky-900/40',
    icon: 'bg-sky-500/15 text-sky-600 dark:text-sky-400 border-sky-500/25',
    label: 'text-sky-800 dark:text-sky-300',
    value: 'text-sky-600 dark:text-sky-400',
  },
  rose: {
    card: 'bg-rose-50/70 dark:bg-rose-950/20 border-rose-200/80 dark:border-rose-900/40',
    icon: 'bg-rose-500/15 text-rose-600 dark:text-rose-400 border-rose-500/25',
    label: 'text-rose-800 dark:text-rose-300',
    value: 'text-rose-600 dark:text-rose-400',
  },
  amber: {
    card: 'bg-amber-50/70 dark:bg-amber-950/20 border-amber-200/80 dark:border-amber-900/40',
    icon: 'bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/25',
    label: 'text-amber-800 dark:text-amber-300',
    value: 'text-amber-600 dark:text-amber-400',
  },
  indigo: {
    card: 'bg-indigo-50/70 dark:bg-indigo-950/20 border-indigo-200/80 dark:border-indigo-900/40',
    icon: 'bg-indigo-500/15 text-indigo-600 dark:text-indigo-400 border-indigo-500/25',
    label: 'text-indigo-800 dark:text-indigo-300',
    value: 'text-indigo-600 dark:text-indigo-400',
  },
  violet: {
    card: 'bg-violet-50/70 dark:bg-violet-950/20 border-violet-200/80 dark:border-violet-900/40',
    icon: 'bg-violet-500/15 text-violet-600 dark:text-violet-400 border-violet-500/25',
    label: 'text-violet-800 dark:text-violet-300',
    value: 'text-violet-600 dark:text-violet-400',
  },
} as const;

export type MetricTone = keyof typeof TONE;

export interface MetricTileProps {
  label: string;
  value: ReactNode;
  /** The quiet line under the figure — a share, a caption, a denominator. */
  hint?: ReactNode;
  icon: ReactNode;
  tone?: MetricTone;
  /**
   * Sits against the trailing edge, opposite the icon — a ring, a sparkline, a
   * small badge. For the same figure said a second way, never for a second
   * figure: the tile has one label and one value, and a chart that needs its
   * own caption belongs in a panel rather than in here.
   */
  trailing?: ReactNode;
  isLoading?: boolean;
  className?: string;
}

/**
 * One headline figure, icon first.
 *
 * The tile the teacher console's attendance screens read across the top: a
 * tinted icon on the leading edge, then the caption, the figure and an optional
 * quiet line under it. Distinct from `FigureTile` (neutral panel, icon opposite
 * the label) and from `StatCard` (a whole flooded panel, for a dashboard of two
 * or three) — this one is built for a row of five or six above a table.
 *
 * The colour never carries the meaning on its own: every tile says in words
 * what it is counting, so "Absent" reads as absent without the rose.
 */
export function MetricTile({
  label,
  value,
  hint,
  icon,
  tone = 'indigo',
  trailing,
  isLoading = false,
  className,
}: MetricTileProps) {
  const style = TONE[tone];

  return (
    <div
      className={cn(
        'card flex items-center gap-2.5 rounded-[14px] p-2.5 shadow-2xs sm:gap-3.5 sm:rounded-[16px] sm:px-4 sm:py-2.5',
        style.card,
        className,
      )}
    >
      <span
        className={cn(
          'grid h-9 w-9 shrink-0 place-items-center rounded-[10px] border sm:h-11 sm:w-11 sm:rounded-[12px]',
          style.icon,
        )}
        aria-hidden
      >
        {icon}
      </span>

      <div className="flex min-w-0 flex-col justify-center">
        <span
          className={cn(
            'block truncate text-[12px] font-bold tracking-tight sm:text-[14px] sm:tracking-wide',
            style.label,
          )}
          // The label and hint are cut to one line on a narrow tile; hovering
          // reads the whole of it.
          title={label}
        >
          {label}
        </span>

        {isLoading ? (
          <Skeleton className="mt-1 h-7 w-16 rounded-field" />
        ) : (
          <p className={cn('mt-0.5 text-2xl font-black leading-tight tabular-nums', style.value)}>
            {value}
          </p>
        )}

        {hint !== undefined && !isLoading && (
          <span
            className={cn('mt-0.5 block truncate text-2xs font-bold sm:text-xs', style.value)}
            title={typeof hint === 'string' || typeof hint === 'number' ? String(hint) : undefined}
          >
            {hint}
          </span>
        )}
      </div>

      {trailing !== undefined && !isLoading && (
        <span className="ml-auto shrink-0 pl-1">{trailing}</span>
      )}
    </div>
  );
}
